/* =========================================================================== *\
 __      __.__     ___________           .__    ____ ________  ________.________
/  \    /  \__| ___\__    ___/___   ____ |  |__/_   /   __   \/   __   \   ____/
\   \/\/   /  |/    \|    |_/ __ \_/ ___\|  |  \|   \____    /\____    /____  \ 
 \        /|  |   |  \    |\  ___/\  \___|   Y  \   |  /    /    /    //       \
  \__/\  / |__|___|  /____| \___  >\___  >___|  /___| /____/    /____//______  /
       \/          \/           \/     \/     \/                             \/ 
\* =========================================================================== */

import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import PropTypes from "prop-types";

// @mui material components
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import IconButton from "@mui/material/IconButton";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import ListItem from "@mui/material/ListItem";
import ListItemAvatar from "@mui/material/ListItemAvatar";
import ListItemText from "@mui/material/ListItemText";
import ListItemButton from "@mui/material/ListItemButton";
import Avatar from "@mui/material/Avatar";
import { blue } from "@mui/material/colors";

// Material Dashboard 2 PRO React components
import MDInput from "components/MDInput";
import MDSelect from "components/MDSelect";
import MDButton from "components/MDButton";
import MDDatePicker from "components/MDDatePicker";

// Service
import { APP_STATE } from "services/Define";
import { FetchData } from "services/Request";
import * as Func from "services/Function";

const initAttr = {
  id: "",
  name: "",
  customer_id: "",
  saleman_id: "",
  color: "info",
  date_start: Func.dateNow(),
  time_start: Func.timeNow(),
  date_stop: Func.dateNow(),
  time_stop: Func.timeNow(),
  enable: "Y",
  created_at: "",
  updated_at: "",
};

const cboColor = [
  { id: "primary", name: "ชมพู" },
  { id: "secondary", name: "เทา" },
  { id: "info", name: "น้ำเงิน" },
  { id: "success", name: "เขียว" },
  { id: "warning", name: "เหลือง" },
  { id: "error", name: "แดง" },
];

function MeetingAdd({ parent, callbackOpen, callbackNotify }) {
  const mod = "meeting";
  const app = useSelector((state) => state[APP_STATE]);
  const { t } = useTranslation();
  const [attr, setAttr] = useState(initAttr);
  const [open, setOpen] = useState(false);
  const [confirm, setConfirm] = useState(false);

  const init = () => {
    setAttr(initAttr);
    setOpen(true);
  };

  const addConfirm = () => {
    if (!Func.verifyLength(parent.id, 1)) return;

    setConfirm(true);
  };

  const add = () => {
    setConfirm(false);
    callbackOpen(false);
    setOpen(false);

    FetchData("PUT", `/${mod}`, app.token, {
      name: attr.name,
      customer_id: parent.id,
      date_start: attr.date_start,
      time_start: attr.time_start,
      date_stop: attr.date_stop,
      time_stop: attr.time_stop,
      color: attr.color,
    }).then((result) => {
      if (result.status === "ok") {
        callbackNotify({ open: true, title: t(`alert.addcomplete`), color: "success" });
      } else {
        callbackNotify({ open: true, title: t(`alert.saveerror`), color: "error" });
      }
    });
  };

  const renderInfo = () => {
    return (
      <Grid container spacing={2}>
        <Grid item xs={12}>
          <MDInput
            label="ลูกค้า"
            value={parent.name}
            variant="standard"
            inputProps={{
              readOnly: true,
            }}
            fullWidth
          />
        </Grid>
        <Grid item xs={12}>
          <MDSelect
            label="สี *"
            labelDefault={t(`element.please_select`)}
            data={cboColor}
            value={attr.color}
            onChange={(e) => setAttr({ ...attr, color: e.target.value })}
            inputProps={{
              autoComplete: "off",
            }}
          />
        </Grid>
        <Grid item xs={7}>
          <MDDatePicker
            onChange={([date]) => {
              setAttr({ ...attr, date_start: Func.dateFormat(date) });
            }}
            value={new Date(attr.date_start)}
            input={{
              label: "เริ่มต้น",
              variant: "standard",
              fullWidth: true,
            }}
          />
        </Grid>
        <Grid item xs={5}>
          <MDInput
            label="เวลา"
            onChange={(e) => setAttr({ ...attr, time_start: e.target.value })}
            value={attr.time_start}
            variant="standard"
            fullWidth
            type="time"
            inputProps={{
              autoComplete: "off",
            }}
          />
        </Grid>
        <Grid item xs={7}>
          <MDDatePicker
            onChange={([date]) => {
              setAttr({ ...attr, date_stop: Func.dateFormat(date) });
            }}
            value={new Date(attr.date_stop)}
            input={{
              label: "สิ้นสุด",
              variant: "standard",
              fullWidth: true,
            }}
          />
        </Grid>
        <Grid item xs={5}>
          <MDInput
            label="เวลา"
            onChange={(e) => setAttr({ ...attr, time_stop: e.target.value })}
            value={attr.time_stop}
            variant="standard"
            fullWidth
            type="time"
            inputProps={{
              autoComplete: "off",
            }}
          />
        </Grid>
      </Grid>
    );
  };

  const renderDialog = () => {
    return (
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        fullScreen
      >
        <AppBar sx={{ position: "relative" }}>
          <Toolbar>
            <IconButton
              edge="start"
              color="inherit"
              onClick={() => setOpen(false)}
              aria-label="close"
            >
              <Icon>arrow_back_ios</Icon>
            </IconButton>
            <Typography sx={{ ml: 2, flex: 1 }} variant="h6" component="div">
              สร้างการติดตาม
            </Typography>
          </Toolbar>
        </AppBar>
        <DialogContent dividers>{renderInfo()}</DialogContent>
        <DialogActions>
          {confirm ? (
            <Grid container>
              <Grid item xs={6}>
                <MDButton variant="text" color="error" onClick={() => setConfirm(false)}>
                  <Icon>cancel</Icon>&nbsp;{t(`element.cancel`)}
                </MDButton>
              </Grid>
              <Grid item xs={6} display="flex" justifyContent="flex-end">
                <MDButton variant="outlined" color="success" onClick={add}>
                  <Icon>save</Icon>&nbsp;{t(`element.saveconfirm`)}
                </MDButton>
              </Grid>
            </Grid>
          ) : (
            <MDButton variant="text" color="info" onClick={addConfirm}>
              <Icon>save</Icon>&nbsp;{t(`element.save`)}
            </MDButton>
          )}
        </DialogActions>
      </Dialog>
    );
  };

  const renderButton = () => {
    return (
      <ListItem disableGutters>
        <ListItemButton autoFocus onClick={init}>
          <ListItemAvatar>
            <Avatar sx={{ bgcolor: blue[100], color: blue[600] }}>
              <Icon>calendar_month</Icon>
            </Avatar>
          </ListItemAvatar>
          <ListItemText primary="สร้างการนัดหมาย" />
        </ListItemButton>
      </ListItem>
    );
  };

  return (
    <div>
      {renderButton()}
      {renderDialog()}
    </div>
  );
}

MeetingAdd.propTypes = {
  parent: PropTypes.shape({
    id: PropTypes.string.isRequired,
    name: PropTypes.string.isRequired,
  }).isRequired,
  callbackOpen: PropTypes.func.isRequired,
  callbackNotify: PropTypes.func.isRequired,
};

export default MeetingAdd;
